import {
  buildFocMemberApplyPayload,
  mapFocProfileResponseToKolProfile,
} from '@/lib/helpers/kol-profile.helper'
import { ApiResponse, httpClient } from '@/services/client/http-client'
import {
  CampaignRegistrationPayload,
  CampaignRegistrationResponse,
  FocCampaignRegisterPayload,
  FocCampaignSubmitUgcPayload,
  FocCampaignUgcHistoryItem,
  FocCampaignUpdateRegistrationPayload,
  FocProfileApplyResponseData,
  FocProfileUpdatePayload,
  KolProfile,
  SaveKolProfileResponse,
} from '@/types/campaign'

export const __cFocCampaignService = {
  registerCampaign: async (
    payload: CampaignRegistrationPayload
  ): Promise<CampaignRegistrationResponse> => {
    const body: FocCampaignRegisterPayload = {
      selected_products: payload.products.map((product) => ({
        product_id: product.productId,
        variant_id: product.variantId,
      })),
      phone: payload.phone,
      email: payload.email,
      address: payload.address,
      city: payload.city,
      district: payload.district,
      ward: payload.ward,
      fullname: payload.fullName,
    }

    await httpClient.V4.post<ApiResponse>(
      `/foc-campaigns/${payload.campaignId}/register`,
      body
    )

    return { success: true, data: { registrationId: '', status: 'pending' } }
  },

  saveKolProfile: async (
    payload: KolProfile
  ): Promise<SaveKolProfileResponse> => {
    const response = await httpClient.V4.post<
      ApiResponse<FocProfileApplyResponseData>
    >('/foc-profiles/apply', buildFocMemberApplyPayload(payload))

    return {
      success: true,
      data: response.data
        ? mapFocProfileResponseToKolProfile(response.data)
        : payload,
    }
  },

  getMyProfile: async (): Promise<FocProfileApplyResponseData | null> => {
    try {
      const response =
        await httpClient.V4.get<ApiResponse<FocProfileApplyResponseData>>(
          '/foc-profiles/me'
        )
      return response.data ?? null
    } catch {
      // Chưa đăng ký KOL (404) hoặc lỗi tạm thời -> coi như chưa có hồ sơ.
      return null
    }
  },

  // Cập nhật 1 phần hồ sơ (chỉ field đã sửa) - dùng ở trang tài khoản, khác
  // với saveKolProfile (POST /apply) vốn cần gửi lại toàn bộ hồ sơ.
  updateMyProfile: async (
    payload: FocProfileUpdatePayload
  ): Promise<SaveKolProfileResponse> => {
    const response = await httpClient.V4.put<
      ApiResponse<FocProfileApplyResponseData>
    >('/foc-profiles/me', payload)

    if (!response.data) {
      throw new Error('Cập nhật hồ sơ thất bại')
    }

    return {
      success: true,
      data: mapFocProfileResponseToKolProfile(response.data),
    }
  },

  // Nộp UGC cho 1 lượt đăng ký - {registrationId} là _id của bản ghi
  // registration (GET /foc-campaigns/me/registrations), không phải campaign_id.
  submitUgc: async (
    registrationId: string,
    payload: FocCampaignSubmitUgcPayload
  ): Promise<void> => {
    await httpClient.V4.post<ApiResponse>(
      `/foc-campaigns/registrations/${registrationId}/ugcs`,
      payload
    )
  },

  // Lịch sử các lần nộp UGC của 1 lượt đăng ký, mới nhất trước.
  getUgcHistory: async (
    registrationId: string
  ): Promise<readonly FocCampaignUgcHistoryItem[]> => {
    const { data } = await httpClient.V4.get<{
      data: readonly FocCampaignUgcHistoryItem[]
    }>(`/foc-campaigns/registrations/${registrationId}/ugcs`)
    return data ?? []
  },

  // Huỷ 1 lần nộp UGC đang chờ duyệt - {ugcId} là _id của bản ghi UGC
  // (lấy từ getUgcHistory), không phải campaign_id/registration_id.
  cancelUgc: async (registrationId: string, ugcId: string): Promise<void> => {
    await httpClient.V4.delete<ApiResponse>(
      `/foc-campaigns/registrations/${registrationId}/ugcs/${ugcId}`
    )
  },

  // Cập nhật thông tin nhận hàng của 1 lượt đăng ký - chỉ cho phép khi
  // registration đang ở trạng thái "pending" (chưa duyệt).
  updateRegistrationInfo: async (
    registrationId: string,
    payload: FocCampaignUpdateRegistrationPayload
  ): Promise<void> => {
    await httpClient.V4.put<ApiResponse>(
      `/foc-campaigns/registrations/${registrationId}`,
      payload
    )
  },
}
